import { computed, ref } from "vue";
import DashboardDataService from "@/services/dashboard/DashboardDataService";
import type { DashboardMainResponse } from "@/types/dashboard/DashboardTypes";
import { t } from "@/i18n";
import { formatDateTime } from "@/utils/date";

const sharedData = ref<DashboardMainResponse | null>(null);
const sharedLoading = ref(false);
const sharedError = ref<string | null>(null);
let fetchPromise: Promise<DashboardMainResponse | null> | null = null;
let requestVersion = 0;

export function resetDashboardMain(): void {
  requestVersion += 1;
  sharedData.value = null;
  sharedLoading.value = false;
  sharedError.value = null;
  fetchPromise = null;
}

async function fetchDashboardMain(): Promise<DashboardMainResponse | null> {
  if (sharedData.value) {
    return sharedData.value;
  }

  if (fetchPromise) {
    return fetchPromise;
  }

  sharedLoading.value = true;
  sharedError.value = null;
  const currentRequestVersion = requestVersion;

  fetchPromise = DashboardDataService.getMain()
    .then((response) => {
      if (currentRequestVersion !== requestVersion) {
        return null;
      }
      const payload = response.data;
      if (!payload.status || !payload.data) {
        throw new Error(payload.message || t("dashboard.error.loadFailed"));
      }
      sharedData.value = payload.data;
      return payload.data;
    })
    .catch((error: { message?: string }) => {
      if (currentRequestVersion === requestVersion) {
        sharedError.value = error.message || t("dashboard.error.generic");
      }
      return null;
    })
    .finally(() => {
      if (currentRequestVersion === requestVersion) {
        sharedLoading.value = false;
        fetchPromise = null;
      }
    });

  return fetchPromise;
}

export function useDashboardMain() {
  const profile = computed(() => sharedData.value?.profile);
  const counters = computed(() => sharedData.value?.counters);
  const incoming = computed(() => sharedData.value?.incoming ?? []);
  const outgoing = computed(() => sharedData.value?.outgoing ?? []);
  const tasksToday = computed(() => sharedData.value?.tasksToday ?? []);
  const overdueTasks = computed(() => sharedData.value?.overdueTasks ?? []);
  const tasksTodayCount = computed(
    () => sharedData.value?.tasksTodayCount ?? tasksToday.value.length
  );
  const overdueTasksCount = computed(
    () => sharedData.value?.overdueTasksCount ?? overdueTasks.value.length
  );

  const reload = async () => {
    resetDashboardMain();
    return fetchDashboardMain();
  };

  return {
    data: sharedData,
    loading: sharedLoading,
    error: sharedError,
    profile,
    counters,
    incoming,
    outgoing,
    tasksToday,
    overdueTasks,
    tasksTodayCount,
    overdueTasksCount,
    reload,
  };
}

export function getGreetingByTime(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return t("dashboard.greeting.morning");
  }
  if (hour >= 12 && hour < 18) {
    return t("dashboard.greeting.afternoon");
  }
  return t("dashboard.greeting.evening");
}

export function formatDashboardDateTime(value?: string | null): string {
  return value ? formatDateTime(value) : t("dashboard.emptyValue");
}

export function formatDashboardDate(value?: string | null): string {
  return value ? formatDateTime(value) : t("dashboard.emptyValue");
}

export function getFirstName(fullName?: string): string {
  if (!fullName) {
    return "";
  }
  return fullName.trim().split(/\s+/)[0] ?? fullName;
}
